import { Link } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import {
  AmplifyButton,
  AmplifyEmailField,
  AmplifyLabel,
  AmplifyPasswordField,
  AmplifySection,
} from '@aws-amplify/ui-react';
import { Auth, Hub, I18n } from 'aws-amplify';
import {
  AuthState,
  AuthStateHandler,
  AUTH_STATE_CHANGE_EVENT,
  TOAST_AUTH_ERROR_EVENT,
  Translations,
  UI_AUTH_CHANNEL,
} from '@aws-amplify/ui-components';
import Logo from '../components/Logo';
import { handleSignIn } from '../utils/handleSignIn';

const RECAPTCHA_ERROR = 'ReCaptcha validation did not succeed.';
const USER_LAMBDA_VALIDATION_EXCEPTION = 'UserLambdaValidationException';
const { REACT_APP_RECAPTCHA_SITE_KEY = '' } = process.env;

type setStringStateType = (password: string) => void;
interface IProps {
  signInUsername: string;
  setUsername: setStringStateType;
  signInPassword: string;
  setPassword: setStringStateType;
}

export default function SignUp(props: IProps) {
  let { signInUsername, signInPassword } = props;
  const { setUsername, setPassword } = props;
  let recaptchaToken = '';

  let captcha: ReCAPTCHA;
  // This is used to reset the captcha when the window reloads.
  // Otherwise, the recaptcha box remains checked, but the value is lost.
  const setCaptcha = (ref: ReCAPTCHA | null) => {
    if (ref) {
      captcha = ref;
      captcha?.reset();
    }
  };

  const onReCaptchaChange = (value: any) => {
    recaptchaToken = value;
  };

  const dispatchAuthStateChangeEvent: AuthStateHandler = (
    nextAuthState: AuthState,
    data?: object,
  ) => {
    Hub.dispatch(UI_AUTH_CHANNEL, {
      event: AUTH_STATE_CHANGE_EVENT,
      message: nextAuthState,
      data,
    });
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    if (event) {
      event.preventDefault();
    }
    const signupParams: any = {
      username: signInUsername,
      password: signInPassword,
      attributes: {
        email: signInUsername,
      },
      validationData: { recaptchaToken },
    };
    try {
      const data = await Auth.signUp({
        username: signInUsername,
        password: signInPassword,
        attributes: {
          email: signInUsername,
        },
        validationData: { recaptchaToken },
      });
      if (!data) {
        throw new Error(Translations.SIGN_UP_FAILED);
      }
      if (data.userConfirmed) {
        await handleSignIn(
          signupParams.username,
          signupParams.password,
          dispatchAuthStateChangeEvent,
        );
      } else {
        dispatchAuthStateChangeEvent(AuthState.ConfirmSignUp, { ...data.user, signupParams });
      }
    } catch (err) {
      const error = err as Error;
      let message = I18n.get(error.message) ?? error.message ?? '';
      if (error.name === USER_LAMBDA_VALIDATION_EXCEPTION) {
        const splitMessage = message.split('#');
        message = splitMessage[splitMessage.length - 1];
      }
      if (message === RECAPTCHA_ERROR) {
        // Sometimes the reCaptcha token expires if the form sits to long before submitting.
        // The ReCAPTCHA should detect and reset, but sometimes does not reset.
        // This resets the checkbox if a captcha error occurs without resetting.
        captcha?.reset();
      }
      Hub.dispatch(UI_AUTH_CHANNEL, {
        event: TOAST_AUTH_ERROR_EVENT,
        message,
      });
    }
  };

  const handleEmailChange = (event: Event): void => {
    const { value } = event?.target as any;
    signInUsername = value;
    setUsername(signInUsername);
  };

  const handlePasswordChange = (event: Event): void => {
    const { value } = event?.target as any;
    signInPassword = value;
    setPassword(signInPassword);
  };

  return (
    <div slot="sign-up" className="flex flex-col items-center py-24">
      <Link to="/">
        <Logo className="w-40 mb-8 -mt-4" />
      </Link>
      <form onSubmit={handleSubmit}>
        <AmplifySection>
          <div style={{ margin: '1rem 0 1.5rem 0' }}>
            <h3
              style={{
                color: 'var(--header-color)',
                fontSize: 'var(--header-size)',
                fontWeight: 700,
                marginBottom: '2.25rem',
              }}
            >
              Create an Account
            </h3>
            <AmplifyEmailField
              typeof="email"
              label="Email"
              placeholder="Email Address"
              required
              value={signInUsername}
              handleInputChange={handleEmailChange}
              style={{
                fontSize: 'var(--amplify-text-sm)',
                height: '3.125rem',
                lineHeight: '1.1',
              }}
            />
            <AmplifyPasswordField
              typeof="password"
              label="Password"
              placeholder="••••••••"
              required
              value={signInPassword}
              handleInputChange={handlePasswordChange}
              style={{
                fontSize: 'var(--amplify-text-sm)',
                height: '3.125rem',
                lineHeight: '1.1',
              }}
            />
            <div
              style={{
                display: 'flex',
                justifyItems: 'center',
                justifyContent: 'center',
                flexDirection: 'row',
                marginBottom: '2rem',
              }}
            >
              <ReCAPTCHA
                sitekey={REACT_APP_RECAPTCHA_SITE_KEY}
                onChange={onReCaptchaChange}
                ref={setCaptcha}
              />
            </div>
          </div>
          <div>
            <div
              style={{
                alignItems: 'baseline',
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                textAlign: 'left',
              }}
            >
              <span>
                <AmplifyLabel
                  style={{
                    marginRight: 2,
                    color: '#828282',
                    fontSize: '0.875rem',
                    textSizeAdjust: '100%',
                  }}
                  data-test="hello"
                >
                  Have an account?
                </AmplifyLabel>
                <AmplifyButton
                  style={{
                    minWidth: 0,
                    fontSize: '0.875rem',
                  }}
                  variant="anchor"
                  onClick={() =>
                    Hub.dispatch(UI_AUTH_CHANNEL, {
                      event: AUTH_STATE_CHANGE_EVENT,
                      message: AuthState.SignIn,
                    })
                  }
                  data-test="sign-up-sign-in-link"
                >
                  Sign In
                </AmplifyButton>
              </span>
              <AmplifyButton
                type="submit"
                variant="button"
                style={{ marginLeft: 'auto', textSizeAdjust: '100%' }}
              >
                Sign Up
              </AmplifyButton>
            </div>
          </div>
        </AmplifySection>
      </form>
      <a
        className="text-white hover:underline"
        href="https://docs.api.bible/terms-and-conditions/"
        target="_blank"
        rel="noreferrer"
      >
        View terms and conditions of use.
      </a>
    </div>
  );
}
